import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/Link";

const CustomerAccountsModal = ({ open, onClose, customer }) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Reset search term when modal closes or customer changes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
    }
  }, [open, customer]);

  const accountEntries = useMemo(() => {
    const rawAccountNumbers = customer?.account_numbers;

    // Legacy shape: ["123", "456"]
    if (Array.isArray(rawAccountNumbers)) {
      return rawAccountNumbers.map((accountNumber) => ({
        accountNumber: String(accountNumber || ""),
        company: "",
        status: "",
      }));
    }

    // New API shape: { [accountNumber]: { account_number, company, status } }
    if (rawAccountNumbers && typeof rawAccountNumbers === "object") {
      return Object.entries(rawAccountNumbers).map(([key, account]) => ({
        accountNumber: String(account?.account_number || key || ""),
        company: String(account?.company || ""),
        status: String(account?.status || ""),
      }));
    }

    return [];
  }, [customer]);

  // Filter account numbers based on search term
  const filteredAccountNumbers = useMemo(() => {
    if (!searchTerm.trim()) return accountEntries;
    const searchLower = searchTerm.toLowerCase();
    return accountEntries.filter(
      ({ accountNumber, company }) =>
        accountNumber?.toLowerCase().includes(searchLower) ||
        company?.toLowerCase().includes(searchLower)
    );
  }, [accountEntries, searchTerm]);

  return (
    <Modal
      open={open && !!customer}
      onClose={onClose}
      aria-labelledby="customer-accounts-modal-title"
      aria-describedby="customer-accounts-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 600,
          maxHeight: "80vh",
          overflow: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "1.25rem",
              }}
            >
              Account Numbers{customer?.customer_name ? ` - ${customer.customer_name}` : ""}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "0.875rem",
                mt: 0.5,
              }}
            >
              {accountEntries.length} account{accountEntries.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#374151",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {accountEntries.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontSize: "0.875rem",
                }}
              >
                No accounts found for this customer.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Subtle Header with Search */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "#64748b",
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Account Numbers
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9ca3af",
                      fontSize: "0.7rem",
                    }}
                  >
                    {filteredAccountNumbers.length} of {accountEntries.length}
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  placeholder="Search account number or organization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.75rem",
                      backgroundColor: "#f8fafc",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "6px 12px",
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>

              {/* Account Numbers List */}
              {filteredAccountNumbers.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#9ca3af",
                      fontSize: "0.75rem",
                    }}
                  >
                    No account numbers match your search.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxHeight: "400px",
                    overflowY: "auto",
                    pr: 0.5,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f5f9",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#cbd5e1",
                      borderRadius: "3px",
                      "&:hover": {
                        backgroundColor: "#94a3b8",
                      },
                    },
                  }}
                >
                  {filteredAccountNumbers.map((account, index) => {
                    const hasPairingMeta = Boolean(account.company || account.status);
                    return (
                    <Box
                      key={account.accountNumber || index}
                      sx={{
                        p: 1.5,
                        borderRadius: "6px",
                        backgroundColor: index % 2 === 0 ? "#f8fafc" : "#ffffff",
                        border: "1px solid #e2e8f0",
                        "&:hover": {
                          backgroundColor: "#f0f9ff",
                          borderColor: "#3b82f6",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "#1e293b",
                              fontFamily: "monospace",
                              fontWeight: 500,
                            }}
                          >
                            {account.accountNumber}
                          </Typography>
                          {account.company ? (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: "#64748b",
                                mt: 0.5,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "100%",
                              }}
                            >
                              {account.company}
                            </Typography>
                          ) : null}
                        </Box>

                        {hasPairingMeta ? (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: "999px",
                              border: "1px solid #bfdbfe",
                              backgroundColor: "#eff6ff",
                              color: "#1d4ed8",
                              flexShrink: 0,
                            }}
                          >
                            <LinkIcon sx={{ fontSize: 12 }} />
                            <Typography
                              sx={{
                                fontSize: "0.675rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                              }}
                            >
                              Paired
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

CustomerAccountsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
};

export default CustomerAccountsModal;

